import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from '../../locales/en.json';
import pl from '../../locales/pl.json';
import { Language } from '../../enums/Language';

export const LANG_STORAGE_KEY = 'menusso-lang';
export const SUPPORTED_LANGUAGES = ['en', 'pl'] as const;

// Maps the UI language codes to the backend language codes (see enums/Language.ts).
const UI_TO_BACKEND_LANGUAGE: Record<string, string> = {
  en: 'ENG',
  pl: 'POL',
};

const DEFAULT_BACKEND_LANGUAGE = 'ENG';

const isSupported = (lang: string | null | undefined): lang is string =>
  !!lang && (SUPPORTED_LANGUAGES as readonly string[]).includes(lang);

// First of the browser's preferred languages that we support (pl-PL -> pl).
// Anything else gets English.
const getBrowserLanguage = (): string => {
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  const match = preferred.map((l) => l?.split('-')[0].toLowerCase()).find(isSupported);
  return match ?? 'en';
};

// A language picked manually in the LanguageSwitcher wins; otherwise follow the browser.
export const getInitialLanguage = (): string => {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    // localStorage unavailable (e.g. private mode) — fall back to the browser language
  }
  return getBrowserLanguage();
};

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    pl: { translation: pl },
  },
  lng: getInitialLanguage(),
  fallbackLng: 'pl',
  supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
  interpolation: { escapeValue: false },
  returnNull: false,
});

// Returns the backend language code for the language currently selected in the
// navbar. Usable outside React (e.g. in the dataProvider) since it reads the
// live i18next instance and falls back to the stored/default language.
export const getBackendLanguage = (): string => {
  const current = (i18n.language || getInitialLanguage()).split('-')[0];
  return UI_TO_BACKEND_LANGUAGE[current] ?? DEFAULT_BACKEND_LANGUAGE;
};

const PINNED_LANGUAGES = ['ENG', 'POL'];

// Language select options: English and Polish pinned on top, the rest sorted
// alphabetically by their name in the current UI language.
export const getLanguageOptions = (t: (key: string) => string): { id: string; name: string }[] => {
  const locale = (i18n.language || getInitialLanguage()).split('-')[0];
  const options = Object.keys(Language).map((id) => ({ id, name: t(`enums.language.${id}`) }));
  const pinned = PINNED_LANGUAGES.map((id) => options.find((o) => o.id === id)!);
  const rest = options
    .filter((o) => !PINNED_LANGUAGES.includes(o.id))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
  return [...pinned, ...rest];
};

export default i18n;
